import { useState } from "react";
import DashboardLayout from "@pma/app/dashboard/layout";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@pma/components/common/card";
import { useParams } from "react-router-dom";
import { Avatar, AvatarFallback } from "@pma/components/common/avatar";
import { Button } from "@pma/components/common/button";
import { AlertCircle, Building, CheckCircle2, Edit, Search } from "lucide-react";
import {
  FINANCIAL_SERVER_URL,
  PRICE_LIST_BY_ID_ENDPOINT,
  PRICE_LIST_EDIT_ENDPOINT,
} from "@pma/utils/api-routes";
import axios from "axios";
import { parseCookies } from "nookies";
import { ACCESS_TOKEN } from "@pma/utils/constants";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useAuth } from "@pma/stores/auth-store";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@pma/components/common/table";
import { DictType } from "@pma/types";
import { toast } from "@pma/hooks/use-toast";
import useTranslator from "@pma/hooks/use-translator";
import { format } from "date-fns";
import { he } from "date-fns/locale";
import PmsLoader from "@pma/components/common/loader";
import { Badge } from "@pma/components/common/badge";
import { Input } from "@pma/components/common/input";
import CreateMainBoQ from "@pma/components/financials/create-main-boq";
import UnauthorizedCard from "@pma/components/layout/unauthorized-card";

const PriceListDetailPage = () => {
  const [newName, setNewName] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const params = useParams();
  const { authConfig } = useAuth();
  const { translate, locale } = useTranslator();

  const isHebrew = locale === "he";
  const orgId = authConfig?.last_organization_id;

  const priceListId = params?.priceListId;
  const accessToken = parseCookies()[ACCESS_TOKEN];

  const getCompanyPriceList = async () => {
    const response = await axios.post(
      FINANCIAL_SERVER_URL + PRICE_LIST_BY_ID_ENDPOINT + "",
      {},
      {
        params: {
          price_id: priceListId,
          organization_id: orgId,
        },
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      },
    );
    return response.data;
  };

  const {
    data: priceLists,
    isLoading,
    refetch,
    isError,
    error,
  } = useQuery({
    queryKey: ["company-price-list", priceListId, newName],
    queryFn: getCompanyPriceList,
    retry: 1,
  });

  const priceListItems = priceLists?.price_items?.items;
  const filteredItems =
    priceListItems?.filter(
      (item: DictType) =>
        item?.number?.includes(searchQuery) ||
        item?.units?.includes(searchQuery) ||
        String(item?.price)?.includes(searchQuery) ||
        item?.description?.toLowerCase().includes(searchQuery.toLowerCase()),
    ) || [];

  const editCompany = async () => {
    const response = await axios.post(
      FINANCIAL_SERVER_URL + PRICE_LIST_EDIT_ENDPOINT,
      {
        name: newName,
        organization_id: orgId,
        price_id: parseInt(String(priceListId)),
      },
      {
        params: {
          organization_id: orgId,
        },
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );

    return response.data;
  };

  const editPriceListMutation = useMutation({
    mutationKey: ["edit-price-list"],
    mutationFn: editCompany,
    onSuccess: () => {
      refetch();
      toast({
        title: translate("Success"),
        description: translate("Price list edited successfully"),
        variant: "success",
      });
      setIsEditing(false);
    },
  });

  const getStatusBadge = (item: DictType) => {
    if (item?.deleted) {
      return (
        <Badge
          variant='destructive'
          className='flex items-center justify-center gap-1 w-fit min-w-[100px]'
        >
          <span className='text-xs'>{translate("Deleted")}</span>
        </Badge>
      );
    } else if (item?.is_item) {
      return (
        <Badge
          variant='default'
          className='flex items-center justify-center gap-1 w-fit min-w-[100px]'
        >
          <CheckCircle2 className='h-3 w-3' /> {translate("Item")}
        </Badge>
      );
    } else {
      return (
        <Badge
          variant='disabled'
          className='flex items-center justify-center gap-1 w-fit min-w-[100px]'
        >
          <AlertCircle className='h-3 w-3' /> {translate("Not an Item")}
        </Badge>
      );
    }
  };

  const priceListError = error as DictType;
  return (
    <DashboardLayout>
      <Card className='relative mt-2 max-w-full overflow-x-auto border-none p-2 md:p-4 shadow-none'>
        <CardContent className='mt-0 h-auto min-h-[50vh] w-full overflow-x-hidden p-0'>
          <div className='space-y-6'>
            <div className='flex flex-col md:flex-row justify-between gap-4'>
              {isError ? (
                <div className='flex items-center'>
                  <UnauthorizedCard message={priceListError?.response?.data?.message} />
                </div>
              ) : (
                <Card className='flex-1'>
                  <CardHeader className='pb-2'>
                    <div className='flex items-start justify-between'>
                      <div className='flex items-center gap-4'>
                        <Avatar className='h-16 w-16 border'>
                          <AvatarFallback className='bg-primary/10 text-primary text-xl'>
                            {priceLists?.price?.name
                              .split(" ")
                              .map((n: string) => n[0])
                              .join("")}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          {isEditing ? (
                            <div className='flex items-center gap-2'>
                              <input
                                className='rounded border font-normal flex-1 mb-1 bg-white/20 px-2 outline-none focus:border-primary'
                                value={newName || priceLists?.price?.name}
                                onChange={(e) => setNewName(e.target.value)}
                                autoFocus
                                onBlur={() => {
                                  if (newName === priceLists?.price?.name) {
                                    setIsEditing(false);
                                    return;
                                  }
                                  editPriceListMutation.mutate();
                                }}
                                onKeyDown={(e) => {
                                  if (e.key !== "Enter") {
                                    return;
                                  }
                                  editPriceListMutation.mutate();
                                }}
                              />
                              <Building size={18} />
                            </div>
                          ) : (
                            <CardTitle className='text-xl'>{priceLists?.price?.name}</CardTitle>
                          )}
                        </div>
                      </div>
                      <div className='flex gap-2'>
                        <Button
                          onClick={() => setIsEditing(!isEditing)}
                          variant='outline'
                          size='sm'
                          asChild
                        >
                          <Edit className='h-4 w-4 mr-1' />
                          {translate("Edit")}
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardFooter className='justify-end'>
                    {!isError && <CreateMainBoQ revalidate={refetch} asLink />}
                  </CardFooter>
                </Card>
              )}
            </div>
            <div className='mt-4'>
              <Card>
                <CardHeader>
                  <CardTitle>{translate("Price list")}</CardTitle>
                </CardHeader>
                {isLoading ? (
                  <PmsLoader />
                ) : (
                  <CardContent>
                    <div className='relative'>
                      <Search className='absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground' />
                      <Input
                        placeholder={translate("Search items") + "..."}
                        className='pl-8 w-full md:w-[300px]'
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                    </div>
                    {!isLoading && filteredItems.length > 0 && (
                      <Table>
                        <TableHeader>
                          <TableRow className='font-semibold'>
                            <TableHead>{translate("Number")}</TableHead>
                            <TableHead>{translate("Description")}</TableHead>
                            <TableHead>{translate("Units")}</TableHead>
                            <TableHead>{translate("Price")}</TableHead>
                            <TableHead>{translate("Status")}</TableHead>
                            <TableHead>{translate("Created")}</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filteredItems?.map((priceList: DictType) => (
                            <TableRow key={priceList?.id}>
                              <TableCell className='font-medium'>{priceList?.number}</TableCell>

                              <TableCell className='text-wrap md:max-w-[300px]'>
                                {priceList?.description || "---"}
                              </TableCell>
                              <TableCell>{priceList?.units || "---"}</TableCell>
                              <TableCell>{priceList?.price}</TableCell>
                              <TableCell>{getStatusBadge(priceList)}</TableCell>
                              <TableCell>
                                {format(priceList?.created_at, "dd MMM yyy", {
                                  locale: isHebrew ? he : undefined,
                                })}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}

                    {!isLoading && filteredItems?.length < 1 && (
                      <div className='flex items-center justify-center p-2'>
                        {translate("No items found")}
                      </div>
                    )}
                  </CardContent>
                )}
              </Card>
            </div>
          </div>
        </CardContent>
      </Card>
    </DashboardLayout>
  );
};

export default PriceListDetailPage;
